import React, { useState } from "react";
import ThemeModal from "./ThemeModal";
import { NavLink, useLocation } from "react-router-dom";
import { useCustomerData, useUserRole } from "../../hooks/useAuth";
import { Store, UploadCloud, X } from "lucide-react";
import packageJson from "../../../package.json";
import { authStorage } from "../../utils/authStorage";
import {
  HomeIcon,
  CubeIcon,
  Squares2X2Icon,
  PresentationChartBarIcon,
  DocumentTextIcon,
  UserIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";


// -------------------- Role-based Menu Definition --------------------
const menuByRole = {
  0: [ // superadmin UI
    { name: "Dashboard", to: "/superadmin/dashboard", icon: HomeIcon },
    { name: "Customer Details", to: "/superadmin/customer-details", icon: UserIcon },
    { name: "Customer Access Control", to: "/superadmin/customer-access-control", icon: ShieldCheckIcon },
  ],
  1: [ // admin UI
    { name: "Dashboard", to: "/admin/dashboard", icon: HomeIcon },
    { name: "Inventory", to: "/admin/inventory", icon: Squares2X2Icon },
    { name: "Pre-Release Tracker", to: "/admin/pre-release-tracker", icon: UploadCloud },
    { name: "Transactions", to: "/admin/transactions", icon: PresentationChartBarIcon },
    { name: "Expenses", to: "/admin/expenses", icon: DocumentTextIcon },
    { name: "Products", to: "/admin/products", icon: CubeIcon },
    { name: "Branches", to: "/admin/branches", icon: Squares2X2Icon },
    { name: "Users", to: "/admin/user-list", icon: UserIcon },
    { name: "Analytics", to: "/admin/analytics", icon: PresentationChartBarIcon },
    { name: "Reports", to: "/admin/reports", icon: DocumentTextIcon },
  ],
  2: [ // POS UI 
    { name: "Sales", to: "/dashboard", icon: HomeIcon },
    { name: "Transactions", to: "/transactions", icon: PresentationChartBarIcon },
    { name: "Expenses", to: "/expenses", icon: DocumentTextIcon },
    { name: "Products Catalog", to: "/products", icon: CubeIcon },
    { name: "Pre release", to: "/pre-release", icon: UploadCloud },
  ],

  3: [ // POS UI 
    { name: "Sales", to: "/dashboard", icon: HomeIcon },
    { name: "Transactions", to: "/transactions", icon: PresentationChartBarIcon },
    { name: "Expenses", to: "/expenses", icon: DocumentTextIcon },
    { name: "Products Catalog", to: "/products", icon: CubeIcon },
    { name: "Pre release", to: "/pre-release", icon: UploadCloud },
  ],
};

// -------------------- Mobile Header Toggle --------------------
// ...existing code...

// -------------------- Sidebar Navigation Links --------------------
function SidebarLinks({ menuItems, closeSidebar, onSettingsClick, isThemeModalOpen, collapsed = false }) {
  const location = useLocation();
  return (
    <nav
      className={`flex-1 overflow-y-auto ${collapsed ? "px-3 space-y-3" : "px-4 space-y-2"}`}
      data-sidebar-links
    >
      {menuItems.map(({ name, to, icon: Icon, isAppearance }) => {
        let navTo = to;
        return (
          <NavLink
            key={name}
            to={navTo}
            title={collapsed ? name : undefined}
            onClick={e => {
              if (isAppearance) {
                e.preventDefault();
                onSettingsClick("appearance");
                closeSidebar();
              } else {
                closeSidebar();
              }
            }}
            className={({ isActive }) => {
              let active = false;
              if (isThemeModalOpen && isAppearance) {
                active = true;
              } else {
                active = isActive;
              }
              return `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${active
                ? "shadow-sm font-medium"
                : "hover:bg-gray-100"
                } ${collapsed ? "justify-center px-3" : ""}`;
            }}
            style={({ isActive }) => {
              let active = false;
              if (isThemeModalOpen && isAppearance) {
                active = true;
              } else {
                active = isActive;
              }
              return {
                background: active ? 'var(--color-primary)' : 'transparent',
              };
            }}
          >
            {({ isActive }) => {
              let active = false;
              if (isThemeModalOpen && isAppearance) {
                active = true;
              } else {
                active = isActive;
              }
              return (
                <>
                <Icon
                  className="h-5 w-5"
                  style={{ color: active ? '#ffffff' : '#6b7280' }}
                />
                  {!collapsed && (
                    <span
                      className="text-sm"
                      style={{ color: active ? '#ffffff' : '#1f2937' }}
                    >
                      {name}
                    </span>
                  )}
                </>
              );
            }}
          </NavLink>
        );
      })}
    </nav>
  );
}

function MobileSidebarHeader({ role, customer, closeSidebar }) {
  const roleConfig = {
    0: {
      label: "Super Admin Workspace",
      subtitle: "System controls",
      icon: ShieldCheckIcon,
    },
    1: {
      label: "Admin Workspace",
      subtitle: customer?.name || "Store operations",
      icon: Store,
    },
    2: {
      label: "Point of Sale",
      subtitle: customer?.name || "Branch operations",
      icon: Store,
    },
    3: {
      label: "Point of Sale",
      subtitle: customer?.name || "Branch operations",
      icon: Store,
    },
  };
  const meta = roleConfig[role] || roleConfig[1];

  return (
    <div className="border-b border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-50 px-4 pb-3 pt-3 lg:hidden">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{meta.label}</p>
          <p className="truncate text-xs text-slate-500">{meta.subtitle}</p>
        </div>
        <button
          type="button"
          onClick={closeSidebar}
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50"
          aria-label="Close sidebar"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

// -------------------- Main Sidebar Component --------------------
export default function Sidebar({ closeSidebar, onBrandUpdated, collapsed = false }) {
  const [isThemeModalOpen, setThemeModalOpen] = useState(false);
  const role = useUserRole();
  const getCustomerData = useCustomerData();
  const storedUser = authStorage.getUser();
  const customer = (role === 1 || role === 2 || role === 3) ? getCustomerData() : null;
  const isManager = storedUser?.user_type === 2
    && String(storedUser?.role_name || "").toLowerCase() === "manager";
  const menuItems = [...(menuByRole[role] || [])]
    .filter(item => !(isManager && (item.name === "User List" || item.name === "Branches" || item.name === "Pre Release Tracker")));

  const handleModalOpen = (type) => {
    if (type === "appearance") {
      setThemeModalOpen(true);
    }
  };
  const handleModalClose = (type) => {
    if (type === "appearance") {
      setThemeModalOpen(false);
    }
  };

  return (
    <>
      <div className={`relative flex h-full flex-col bg-white text-gray-700 pt-2 lg:pt-6`}>
        {!collapsed && (
          <MobileSidebarHeader
            role={role}
            customer={customer}
            closeSidebar={closeSidebar}
          />
        )}
        <SidebarLinks
          menuItems={menuItems}
          closeSidebar={closeSidebar}
          onSettingsClick={handleModalOpen}
          isThemeModalOpen={isThemeModalOpen}
          collapsed={collapsed}
        />
        {!collapsed && (
          <div className="px-4 py-3 text-xs text-gray-500 mt-auto border-t border-gray-200">
            Version {packageJson.version}
          </div>
        )}
      </div>
      <ThemeModal isOpen={isThemeModalOpen} onClose={() => handleModalClose("appearance")} />
    </>
  );
}
